import React, { useState, useMemo, useEffect } from 'react';
import MainLayout from '../../../shared/layouts/MainLayout';
import { Card, Alert, Badge, Loading, Spinner } from '../../../shared/components';
import { useReports } from '../hooks/useReports';
import { useExport } from '../hooks/useExport';
import ExportButtons from '../components/ExportButtons';
import ReportTable from '../components/ReportTable';
import ReportFilters from '../components/ReportFilters';

// Mock data for demonstration since API might return empty array
const mockLowStockData = [
  {
    name: "Insulin Regular",
    current_stock: 15,
    minimum_stock: 20,
    category: "Insulin",
    last_ordered: "2026-02-15",
    supplier: "MedSupply Co."
  },
  {
    name: "Atropine",
    current_stock: 8,
    minimum_stock: 15,
    category: "Emergency",
    last_ordered: "2026-02-10",
    supplier: "PharmaCorp"
  },
  {
    name: "Ciprofloxacin",
    current_stock: 12,
    minimum_stock: 25,
    category: "Antibiotic",
    last_ordered: "2026-02-01",
    supplier: "MedSupply Co."
  },
  {
    name: "Metoprolol",
    current_stock: 5,
    minimum_stock: 10,
    category: "Cardiac",
    last_ordered: "2026-01-28",
    supplier: "HealthMed"
  },
  {
    name: "Amoxicillin",
    current_stock: 0,
    minimum_stock: 30,
    category: "Antibiotic",
    last_ordered: "2026-01-15",
    supplier: "PharmaCorp"
  }
];

const LowStockReportPage = () => {
  const [filters, setFilters] = useState({
    threshold: '',
    category: '',
    stockStatus: 'all' // 'all', 'critical', 'out-of-stock', 'low'
  });
  
  const { data, loading, error, refetch } = useReports('low_stock', filters);
  const exportUtils = useExport();

  // Debug: Log the actual data structure
  useEffect(() => {
    console.log('LowStockReportPage - Full data from API:', data);
  }, [data]);

  // Extract the actual data array from the nested structure or use mock data
  const getRawDataArray = () => {
    if (!data) return [];
    
    // Based on your API response structure
    if (data.data && data.data.data && Array.isArray(data.data.data)) {
      return data.data.data;
    }
    
    // Fallback: try data.data if it's an array
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    // Fallback: try data itself if it's an array
    if (Array.isArray(data)) {
      return data;
    }
    
    // If API returns empty array but we want to show mock data for demonstration
    // Remove this in production when API returns real data
    if (data?.data && Array.isArray(data.data) && data.data.length === 0) {
      console.log('Using mock data for demonstration');
      return mockLowStockData;
    }
    
    return [];
  };

  const rawData = getRawDataArray();

  // Apply local filters
  const filteredData = useMemo(() => {
    if (!rawData.length) return [];
    
    return rawData.filter(item => {
      // Category filter
      if (filters.category && !item.category?.toLowerCase().includes(filters.category.toLowerCase())) {
        return false;
      }
      
      // Threshold filter (minimum stock level)
      if (filters.threshold) {
        const threshold = parseInt(filters.threshold);
        if (parseInt(item.current_stock) > threshold) return false;
      }
      
      // Stock status filter
      if (filters.stockStatus !== 'all') {
        const stockLevel = parseInt(item.current_stock);
        const minStock = parseInt(item.minimum_stock);
        
        switch (filters.stockStatus) {
          case 'critical':
            if (stockLevel > minStock * 0.5) return false;
            break;
          case 'out-of-stock':
            if (stockLevel > 0) return false;
            break;
          case 'low':
            if (stockLevel > minStock) return false;
            break;
          default:
            break;
        }
      }
      
      return true;
    });
  }, [rawData, filters]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = rawData.length;
    const critical = rawData.filter(item => 
      parseInt(item.current_stock) <= parseInt(item.minimum_stock) * 0.5 && 
      parseInt(item.current_stock) > 0
    ).length;
    const outOfStock = rawData.filter(item => parseInt(item.current_stock) === 0).length;
    const lowStock = rawData.filter(item => 
      parseInt(item.current_stock) <= parseInt(item.minimum_stock) && 
      parseInt(item.current_stock) > parseInt(item.minimum_stock) * 0.5
    ).length;
    
    return { total, critical, outOfStock, lowStock };
  }, [rawData]);

  const columns = [
    { 
      header: 'Medicine Name', 
      accessor: 'name',
      render: (value) => <span className="font-medium">{value}</span>
    },
    { 
      header: 'Current Stock', 
      accessor: 'current_stock',
      render: (value, row) => {
        const stockLevel = parseInt(value);
        const minStock = parseInt(row.minimum_stock);
        let colorClass = 'text-gray-900';
        
        if (stockLevel === 0) {
          colorClass = 'text-red-600 font-bold';
        } else if (stockLevel <= minStock * 0.5) {
          colorClass = 'text-red-500 font-semibold';
        } else if (stockLevel <= minStock) {
          colorClass = 'text-orange-500 font-semibold';
        }
        
        return <span className={colorClass}>{value}</span>;
      }
    },
    { header: 'Minimum Stock Level', accessor: 'minimum_stock' },
    { 
      header: 'Category', 
      accessor: 'category',
      render: (value) => value || '-'
    },
    { 
      header: 'Status', 
      accessor: 'status',
      render: (value, row) => {
        const stockLevel = parseInt(row.current_stock);
        const minStock = parseInt(row.minimum_stock);
        const percentage = ((minStock - stockLevel) / minStock * 100).toFixed(0);
        
        if (stockLevel === 0) {
          return (
            <div className="flex flex-col">
              <Badge variant="danger">Out of Stock</Badge>
              <span className="text-xs text-gray-500 mt-1">Need {minStock} units</span>
            </div>
          );
        } else if (stockLevel <= minStock * 0.5) {
          return (
            <div className="flex flex-col">
              <Badge variant="danger">Critical</Badge>
              <span className="text-xs text-gray-500 mt-1">{percentage}% below min</span>
            </div>
          );
        } else if (stockLevel <= minStock) {
          return (
            <div className="flex flex-col">
              <Badge variant="warning">Low</Badge>
              <span className="text-xs text-gray-500 mt-1">{percentage}% below min</span>
            </div>
          );
        }
        return <Badge variant="success">Adequate</Badge>;
      }
    },
    { 
      header: 'Suggested Order', 
      accessor: 'suggested_order',
      render: (value, row) => {
        const stockLevel = parseInt(row.current_stock);
        const minStock = parseInt(row.minimum_stock);
        if (stockLevel < minStock) {
          const suggestedOrder = minStock * 2 - stockLevel;
          return (
            <div className="text-blue-600 font-semibold">
              {suggestedOrder} units
            </div>
          );
        }
        return '-';
      }
    },
    { 
      header: 'Last Ordered', 
      accessor: 'last_ordered',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-'
    }
  ];

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    // Only call refetch if filters affect backend
    if (newFilters.category || newFilters.threshold) {
      refetch();
    }
  };

  const handleReset = () => {
    setFilters({
      threshold: '',
      category: '',
      stockStatus: 'all'
    });
    refetch();
  };

  // Handler functions for exports
  const handleCSVExport = () => {
    exportUtils.exportToCSV(filteredData, 'low_stock_report');
  };

  const handleExcelExport = () => {
    exportUtils.exportToExcel(filteredData, 'low_stock_report');
  };

  const handlePDFExport = () => {
    exportUtils.exportToPDF(
      filteredData, 
      columns, 
      'Low Stock Alert Report', 
      'low_stock_report'
    );
  };

  const handlePrint = () => {
    exportUtils.print();
  };

  if (loading) return <Spinner />;
  if (error) return <Alert type="error" message={error} />;

  return (
    // <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Low Stock Alert Report</h1>
        
        {/* Filters */}
        <ReportFilters
          filters={filters}
          onApply={handleApplyFilters}
          onReset={handleReset}
          showDateRange={false}
        />
        
        {/* Statistics Cards */}
        {rawData.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Total Items</h3>
                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
              </div>
            </Card>
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Out of Stock</h3>
                <p className="text-2xl font-bold text-red-800">{stats.outOfStock}</p>
              </div>
            </Card>
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Critical</h3>
                <p className="text-2xl font-bold text-red-600">{stats.critical}</p>
              </div>
            </Card>
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Low Stock</h3>
                <p className="text-2xl font-bold text-yellow-600">{stats.lowStock}</p>
              </div>
            </Card>
          </div>
        )}
        
        {/* Export Buttons */}
        <ExportButtons
          onExportCSV={handleCSVExport}
          onExportExcel={handleExcelExport}
          onExportPDF={handlePDFExport}
          onPrint={handlePrint}
          disabled={loading}
          showRecordCount={true}
          recordCount={filteredData.length}
        />
        
        {/* Main Table */}
        <Card>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Items Below Minimum Stock Level</h2>
              {filters.category && (
                <span className="text-sm text-gray-500">
                  Category: {filters.category}
                </span>
              )}
            </div>
            
            {filteredData.length > 0 ? (
              <ReportTable
                data={{ data: filteredData }}
                columns={columns}
              />
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <svg 
                  className="mx-auto h-12 w-12 text-gray-400" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
                <p className="text-lg text-gray-600 mt-4">No low stock items found</p>
                <p className="text-sm text-gray-500 mt-2">
                  All medicines are above their minimum stock levels
                </p>
                {rawData.length === 0 && (
                  <p className="text-xs text-gray-400 mt-4">
                    Note: Using mock data for demonstration. API returned empty array.
                  </p>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Restock Suggestions */}
        {filteredData.length > 0 && (
          <Card className="mt-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Restock Suggestions</h3>
              <div className="space-y-3">
                {filteredData.slice(0, 5).map((item, index) => {
                  const stockLevel = parseInt(item.current_stock);
                  const minStock = parseInt(item.minimum_stock);
                  const suggestedOrder = minStock * 2 - stockLevel;
                  const priority = stockLevel === 0 ? 'High' : 
                                  stockLevel <= minStock * 0.5 ? 'High' : 'Medium';
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-sm text-gray-600">
                          Current: {stockLevel} | Min: {minStock} | Suggested: {suggestedOrder} units
                        </p>
                      </div>
                      <Badge variant={priority === 'High' ? 'danger' : 'warning'}>
                        {priority} Priority
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}
      </div>
    // </MainLayout>
  );
};

export default LowStockReportPage;